// Pantalla "Investigaciones" (PRD Bloque M): lo que la IA (o Ian a mano) investigó en una
// fecha (anoche por omisión). Todo sale de research_report(); los filtros de motor, producto,
// estatus y resultado se aplican aquí (el día ya trae pocas filas, no vale la pena ir y venir
// al servidor por cada filtro).
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotify } from "ra-core";
import { Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { box } from "../ficha/FichaTabs";
import { cn } from "@/lib/utils";
import { MobilePage } from "../MobilePage";
import { mxDaysFromToday, mxAt } from "../mxDate";
import { telHref } from "../contactLinks";
import { startCallTimer } from "../calls/callTimer";
import { ThermoBar } from "../thermo/thermo";
import { useOffers } from "../useOffers";

const sb = () => getSupabaseClient();

type Row = {
  directory_id: number;
  company_id: number | null;
  contact_id: number | null;
  nombre: string;
  giro: string | null;
  municipio: string | null;
  estado: string | null;
  telefono: string | null;
  dnc: boolean;
  thermo_before: number | null;
  thermo_after: number | null;
  producto_entrada: string | null;
  pct_match: number | null;
  motor: string;
  estatus: "en_cola" | "investigando" | "lista" | "error";
  motivo_error: string | null;
  resultado: "caliente" | "tibio" | "frio" | null;
  confirmados: number;
  sin_confirmar: number;
};
type Report = {
  fecha: string;
  investigadas: number;
  fallidas_n: number;
  nuevas_calientes: number;
  cupos: Record<string, number>;
  filas: Row[];
};

const ayer = () => mxDaysFromToday(-1);

const Stat = ({ label, value }: { label: string; value: number | string }) => (
  <div className={cn(box, "flex flex-col gap-1")}>
    <span className="text-2xl font-extrabold">{value}</span>
    <span className="text-xs text-muted-foreground">{label}</span>
  </div>
);

const CUPO_LABEL: Record<string, string> = {
  gemini: "Gemini",
  gemini_busqueda: "Búsqueda de Gemini",
  groq: "Groq",
  openrouter: "OpenRouter",
  tavily: "Tavily",
  serpapi: "SerpApi",
  deepseek: "DeepSeek",
  claude_api: "Claude API",
};

export const InvestigacionesPage = () => {
  const [fecha, setFecha] = useState(ayer());
  const [motor, setMotor] = useState("");
  const [producto, setProducto] = useState("");
  const [estatus, setEstatus] = useState("");
  const [resultado, setResultado] = useState("");
  const notify = useNotify();
  const qc = useQueryClient();
  const { data: offers } = useOffers();

  const { data: R, isPending } = useQuery({
    queryKey: ["research_report", fecha],
    queryFn: async () => {
      const { data, error } = await sb().rpc("research_report", {
        p_date: fecha,
      });
      if (error) throw error;
      return data as Report;
    },
  });

  const motores = useMemo(
    () => [...new Set((R?.filas ?? []).map((r) => r.motor).filter(Boolean))],
    [R],
  );
  const filas = (R?.filas ?? []).filter(
    (r) =>
      (!motor || r.motor === motor) &&
      (!producto || r.producto_entrada === producto) &&
      (!estatus || r.estatus === estatus) &&
      (!resultado || r.resultado === resultado),
  );
  const offerName = (slug: string | null) =>
    slug ? (offers?.find((o) => o.slug === slug)?.name ?? slug) : "—";

  const reintentar = useMutation({
    mutationFn: async (ids: number[]) => {
      const { error } = await sb().rpc("research_enqueue", { p_ids: ids });
      if (error) throw error;
    },
    onSuccess: () => {
      notify("En cola para investigarse de nuevo", { type: "success" });
      qc.invalidateQueries({ queryKey: ["research_report"] });
    },
    onError: (e: Error) => notify(e.message, { type: "error" }),
  });

  const agregarHoy = useMutation({
    mutationFn: async (r: Row) => {
      if (!r.contact_id) throw new Error("Pasa la empresa al CRM primero");
      const { error } = await sb()
        .from("tasks")
        .insert({
          contact_id: r.contact_id,
          type: "call",
          text: `Llamar: investigación del ${fecha}`,
          due_date: mxAt(mxDaysFromToday(0), "10:00").toISOString(),
        });
      if (error) throw error;
    },
    onSuccess: () => {
      notify("Agregada a Mi día de hoy", { type: "success" });
      qc.invalidateQueries({ queryKey: ["my_day"] });
    },
    onError: (e: Error) => notify(e.message, { type: "error" }),
  });

  const agregarTodasCalientes = async () => {
    const calientes = filas.filter(
      (r) => r.resultado === "caliente" && r.contact_id,
    );
    for (const r of calientes) await agregarHoy.mutateAsync(r);
    notify(`${calientes.length} agregadas a Mi día`, { type: "success" });
  };

  return (
    <MobilePage title="Investigaciones">
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col text-xs text-muted-foreground">
            Fecha
            <input
              type="date"
              className="rounded-md border bg-background px-2 py-1.5 text-sm"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </label>
          <select
            className="rounded-md border bg-background px-2 py-1.5 text-sm"
            value={motor}
            onChange={(e) => setMotor(e.target.value)}
          >
            <option value="">Todos los motores</option>
            {motores.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <select
            className="rounded-md border bg-background px-2 py-1.5 text-sm"
            value={producto}
            onChange={(e) => setProducto(e.target.value)}
          >
            <option value="">Todos los productos</option>
            {offers?.map((o) => (
              <option key={o.slug} value={o.slug}>
                {o.name}
              </option>
            ))}
          </select>
          <select
            className="rounded-md border bg-background px-2 py-1.5 text-sm"
            value={estatus}
            onChange={(e) => setEstatus(e.target.value)}
          >
            <option value="">Todos los estatus</option>
            <option value="lista">Lista</option>
            <option value="error">Fallida</option>
            <option value="en_cola">En cola</option>
            <option value="investigando">Investigando</option>
          </select>
          <select
            className="rounded-md border bg-background px-2 py-1.5 text-sm"
            value={resultado}
            onChange={(e) => setResultado(e.target.value)}
          >
            <option value="">Todos los resultados</option>
            <option value="caliente">Caliente</option>
            <option value="tibio">Tibio</option>
            <option value="frio">Frío</option>
          </select>
        </div>

        {isPending || !R ? (
          <p className="text-sm text-muted-foreground">Cargando…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Stat label="Investigadas" value={R.investigadas} />
              <Stat label="Fallidas" value={R.fallidas_n} />
              <Stat label="Nuevas calientes (≥60)" value={R.nuevas_calientes} />
              <div className={cn(box, "flex flex-col gap-1")}>
                <span className="text-xs font-semibold text-muted-foreground">
                  Cupos gastados
                </span>
                <span className="text-xs">
                  {Object.entries(R.cupos)
                    .filter(([, n]) => n > 0)
                    .map(([k, n]) => `${CUPO_LABEL[k] ?? k}: ${n}`)
                    .join(" · ") || "Ninguno"}
                </span>
              </div>
            </div>

            {filas.some((r) => r.resultado === "caliente") && (
              <Button
                size="sm"
                variant="outline"
                className="self-start"
                onClick={agregarTodasCalientes}
              >
                Agregar todas las calientes a Mi día de hoy
              </Button>
            )}

            <div className="flex flex-col gap-2">
              {!filas.length && (
                <p className="text-sm text-muted-foreground">
                  Sin investigaciones con estos filtros.
                </p>
              )}
              {filas.map((r) => (
                <div
                  key={r.directory_id}
                  className={cn(box, "flex flex-col gap-1.5 text-sm")}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={
                        r.company_id
                          ? `/companies/${r.company_id}/show`
                          : `/directory/${r.directory_id}/show`
                      }
                      className="font-bold hover:text-primary hover:underline"
                    >
                      {r.nombre}
                    </Link>
                    <span className="text-xs text-muted-foreground">
                      {r.giro} · {r.municipio}, {r.estado}
                    </span>
                    {r.estatus === "error" ? (
                      <span className="rounded-full border border-[var(--critical)] px-2 py-0.5 text-xs font-semibold text-[var(--critical)]">
                        Falló: {r.motivo_error}
                      </span>
                    ) : (
                      <span className="rounded-full border px-2 py-0.5 text-xs">
                        {r.estatus}
                      </span>
                    )}
                  </div>
                  {r.estatus === "lista" && (
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span>
                        Termómetro: {r.thermo_before ?? "—"} →{" "}
                        <ThermoBar value={r.thermo_after} mini />
                      </span>
                      <span>Entra por {offerName(r.producto_entrada)}</span>
                      {r.pct_match != null && (
                        <span>{r.pct_match}% de match</span>
                      )}
                      <span>Motor: {r.motor}</span>
                      <span>
                        {r.confirmados} confirmados
                        {r.sin_confirmar > 0
                          ? ` · ${r.sin_confirmar} sin confirmar`
                          : ""}
                      </span>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {r.telefono && !r.dnc && (
                      <Button size="sm" variant="outline" asChild>
                        <a
                          href={telHref(r.telefono)!}
                          onClick={() => startCallTimer()}
                        >
                          <Phone /> Llamar
                        </a>
                      </Button>
                    )}
                    {r.company_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!r.contact_id || agregarHoy.isPending}
                        onClick={() => agregarHoy.mutate(r)}
                      >
                        Agregar a Mi día de hoy
                      </Button>
                    )}
                    {!r.company_id && (
                      <Button size="sm" variant="outline" asChild>
                        <Link to={`/directory/${r.directory_id}/show`}>
                          Pasar al CRM
                        </Link>
                      </Button>
                    )}
                    {r.estatus === "error" && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={reintentar.isPending}
                        onClick={() => reintentar.mutate([r.directory_id])}
                      >
                        Volver a investigar
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </MobilePage>
  );
};
